import { useEffect, useState } from 'react';

const DEFAULT_DEBOUNCE_MS = 300;

/**
 * Returns a debounced copy of `value` that updates after `delayMs` of stability.
 * Used for typeahead lookups so TanStack Query does not fire on every keystroke.
 *
 * The `useEffect` + `useState` pair here is intentional: debouncing is a timer
 * side effect, not derived-state sync (`state.mdc` §2 forbids the latter).
 */
export function useDebouncedValue<T>(value: T, delayMs = DEFAULT_DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);

  // Timer side effect — schedules the delayed publish of the latest input value.
  useEffect(() => {
    const timerId = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timerId);
  }, [value, delayMs]);

  return debounced;
}
